import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { activityPing } from '../redux/slices/trackActivitySlice';
import { sendActivityPingBeacon } from '../api/trackActivityBeacon';

/**
 * usePageTracking Hook
 * 
 * Automatically tracks time spent on a page and sends it to the backend in segments.
 * - Handles Tab Switching (Visibility API)
 * - Sends "Heartbeats" every 10 seconds (prevents data loss on crash)
 * - Tracks time even if started in background tab (waits for visibility)
 * - Convert milliseconds to Minutes (standard backend format)
 * - Sends final data on unmount/page close
 * 
 * @param {string} pageName - The name of the page to track (e.g., 'DASHBOARD')
 * @param {string} platform - The platform 'web' | 'android' | 'ios' (default: 'web')
 * @param {boolean} enabled - Set false to skip tracking entirely (e.g. a guest
 *   viewing an otherwise-protected page — the activity endpoints require auth).
 */


const usePageTracking = (pageName, platform = 'web', enabled = true) => {
    const dispatch = useDispatch();

    // Configuration
    const PING_INTERVAL = 30000; // 30 seconds

    const startTimeRef = useRef(null);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (!enabled) return undefined;

        // Regular foreground heartbeat — a normal XHR is fine while the page is alive.
        const sendData = () => {
            if (!startTimeRef.current) return;

            const now = Date.now();
            const timeSpentMs = now - startTimeRef.current;

            startTimeRef.current = now;

            if (timeSpentMs < 100) return;


            const payload = {
                page_name: pageName,
            };

            dispatch(activityPing(payload));
        };

        // Final flush when the page is hidden/closing. axios is unreliable during
        // teardown, so use fetch keepalive (see trackActivityBeacon) which the
        // browser completes even after unload.
        const flushOnExit = () => {
            if (!startTimeRef.current) return;
            startTimeRef.current = null; // Indicates paused state
            sendActivityPingBeacon(pageName);
        };

        // Initialize based on current visibility state
        if (!document.hidden) {
            startTimeRef.current = Date.now();
            intervalRef.current = setInterval(sendData, PING_INTERVAL);
        } else {
            startTimeRef.current = null; // Don't track if starting in background
        }

        // Handle Visibility Change (Pause when user switches tabs / backgrounds on mobile)
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Tab became hidden: reliably flush data so far and PAUSE timer
                flushOnExit();
                if (intervalRef.current) clearInterval(intervalRef.current);
            } else {
                // Tab became visible: RESUME timer
                startTimeRef.current = Date.now();
                if (intervalRef.current) clearInterval(intervalRef.current); // Safety clear
                intervalRef.current = setInterval(sendData, PING_INTERVAL);
            }
        };

        // Handle Page Hide (tab close / navigation). pagehide is far more reliable
        // than beforeunload and, unlike it, does not disable the bfcache.
        const handlePageHide = () => {
            flushOnExit();
        };

        // Event Listeners
        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("pagehide", handlePageHide);

        // Cleanup on Component Unmount (SPA route change — page still alive, XHR is fine)
        return () => {
            sendData(); // Send remaining partial time
            if (intervalRef.current) clearInterval(intervalRef.current);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            window.removeEventListener("pagehide", handlePageHide);
        };
    }, [dispatch, pageName, platform, enabled]);
};

export default usePageTracking;
